import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageSquare, FileText, Clock, Medal, RefreshCw, Users } from 'lucide-react';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const ClientPortalSection = () => {
  // Reference for parallax effect
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const features = [
    {
      icon: <MessageSquare size={24} className="text-primary" strokeWidth={2} />,
      title: "Directe Communicatie",
      description: "Altijd in direct contact met ons team, zonder e-mails of telefoontjes."
    },
    {
      icon: <FileText size={24} className="text-primary" strokeWidth={2} />,
      title: "Probleemloos Beheer",
      description: "Geen zorgen over problemen - wij beheren alles voor u in één overzichtelijk platform."
    },
    {
      icon: <Clock size={24} className="text-primary" strokeWidth={2} />,
      title: "Snelle Aanpassingen",
      description: "Wijzigingen worden snel voor u doorgevoerd via het portaal zonder wachttijden."
    },
    {
      icon: <Medal size={24} className="text-primary" strokeWidth={2} />,
      title: "Gebruiksvriendelijk",
      description: "Intuïtieve interface die iedereen kan gebruiken, zonder technische kennis."
    },
    {
      icon: <RefreshCw size={24} className="text-primary" strokeWidth={2} />,
      title: "Alles in Één",
      description: "Beheer al uw diensten en projecten in één centraal platform."
    },
    {
      icon: <Users size={24} className="text-primary" strokeWidth={2} />,
      title: "Volgens Prijsplan",
      description: "Toegang tot functionaliteiten die passen bij uw prijsplan met flexibele opties."
    }
  ];

  return (
    <section 
      id="client-portal" 
      ref={sectionRef}
      className="py-16 md:py-24 bg-background"
    >
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            <span className="relative">
              Uniek Aan Ons:
              <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-blue-400 transform -translate-y-1"></span>
            </span>{" "}
            <span className="text-primary">CylostreamHub</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-muted-foreground max-w-3xl mx-auto"
          >
            Ons uitgebreide klantenportaal zorgt voor een naadloze ervaring en direct contact met ons team.
          </motion.p>
        </motion.div>

        <div className="relative">
          <motion.div style={{ y: parallaxY }} className="absolute -right-20 -top-20 w-64 h-64 bg-secondary/10 rounded-full blur-3xl"></motion.div>
          <motion.div style={{ y: parallaxY }} className="absolute -left-20 -bottom-20 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10 mb-12"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="bg-background/80 backdrop-blur border border-border/50 p-6 rounded-xl shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeInUp}
          className="text-center"
        >
          <Button 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg text-lg"
            onClick={() => window.open("https://hub.cylostream.com", "_blank")}
          >
            Ontdek CylostreamHub
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientPortalSection; 